import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Profile, UserRole } from '../types';

export default function UsersManagement() {
  const { profile, company, refreshProfile } = useAuth();
  const { showToast } = useToast();

  const [users,       setUsers]       = useState<Profile[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole,  setInviteRole]  = useState<'developer' | 'tester'>('developer');
  const [sending,     setSending]     = useState(false);
  const [sentTo,      setSentTo]      = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Profile[]>('/api/users');
      setUsers(data);
    } catch {
      showToast('Failed to load company users.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!profile?.company_id) return;
    fetchUsers();
  }, [profile?.company_id, fetchUsers]);

  const updateRole = async (userId: string, role: UserRole) => {
    try {
      await api.patch(`/api/users/${userId}/role`, { role });
      showToast('Role updated.', 'success');
      if (userId === profile?.user_id) await refreshProfile();
      await fetchUsers();
    } catch (err: any) {
      showToast(err.message || 'Failed to update role.', 'error');
    }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim())   { showToast('Please enter an email address.', 'error'); return; }
    if (!company?.invite_code) { showToast('Company invite code not found.', 'error'); return; }

    const email = inviteEmail.trim().toLowerCase();
    setSending(true);

    try {
      const params = new URLSearchParams({
        email,
        companyCode: company.invite_code,
        companyName: company.name ?? '',
        role: inviteRole,
      });
      const inviteLink = `${window.location.origin}/accept-invite?${params.toString()}`;

      await api.post('/api/invite', {
        email,
        role: inviteRole,
        companyName: company.name,
        companyCode: company.invite_code,
        inviteLink,
      });

      setSentTo(email);
      setInviteEmail('');
      showToast(`Invitation sent to ${email}!`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send invitation.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
      </div>

      <div className="form-card">
        <div style={{ marginBottom: 16 }}>
          <div className="form-label">Company</div>
          <div>{company?.name ?? 'Unknown'}</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div className="form-label">Invite Code</div>
          <div style={{ fontWeight: 700, letterSpacing: 1.2 }}>{company?.invite_code ?? 'N/A'}</div>
        </div>

        <form onSubmit={sendInvite} style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16, marginBottom: 24 }}>
          <div className="form-label" style={{ marginBottom: 10 }}>Invite Team Member</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="inviteEmail">Email</label>
              <input
                id="inviteEmail"
                className="form-input"
                type="email"
                value={inviteEmail}
                onChange={e => { setInviteEmail(e.target.value); setSentTo(''); }}
                placeholder="teammate@company.com"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="inviteRole">Role</label>
              <select
                id="inviteRole"
                className="form-select"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as 'developer' | 'tester')}
              >
                <option value="developer">Developer</option>
                <option value="tester">Tester</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? (
                <><span className="btn-spinner" /> Sending…</>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Send Invitation
                </>
              )}
            </button>
          </div>

          {sentTo && (
            <div className="invite-sent-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Invitation sent to <strong>{sentTo}</strong> as <strong>{inviteRole}</strong>.
            </div>
          )}
        </form>

        {loading ? (
          <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Loading users…</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {users.map(u => {
              const isSelf = u.user_id === profile?.user_id;
              return (
                <div key={u.user_id} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>{u.full_name || u.email}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 8 }}>{u.email}</div>
                  <select
                    className="form-select"
                    value={u.role}
                    disabled={isSelf}
                    onChange={e => updateRole(u.user_id, e.target.value as UserRole)}
                  >
                    <option value="admin">Admin</option>
                    <option value="developer">Developer</option>
                    <option value="tester">Tester</option>
                  </select>
                  {isSelf && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
                      You cannot change your own role here.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
